import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setSearch,
  setCountryData,
  setSearchResults,
} from "../../../../store/search/searchSlice";
import { Countries } from "../../Home/CountryFlag";
import { Button } from "../../../common/u1/Button";
import useCountryCode from "../../../../Hooks/useCountryCode";
import { ReactComponent as Magnifier } from "../../../../assets/icons/Magnifier.svg";
import { ReactComponent as GPS } from "../../../../assets/icons/GPS.svg";
import { SearchIconCompact, ChevronDown, CheckIcon } from "../../../../assets/ui/icons";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { cn } from "../../../common/u1/cn";

const GCC_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];

/* u1 search band. Presentation is the redesign's single-row card: country selector, divider, name input, submit. The behaviour is transcribed from the... */
export const SearchBar = ({
  resultsPath = "/search",
  isLoading = false,
  /* "md" is the search page's band. "sm" is the dashboard top bar: the same
     control and the same behaviour, sized to the bar's 46px row so it lines up
     with the bell and the account chip beside it. */
  size = "md",
  /* The phone app bar opens this from a glyph, so the caret has to land in the
     field — otherwise the keyboard never comes up. */
  autoFocus = false,
  /* Whether the field shows the query behind the current results. True on the
     results page, where it describes what is on screen; false in the app bar,
     where the control is only a way to start a new search. */
  reflectQuery = true,
  className = "",
}) => {
  const compact = size === "sm";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { searchQuery, countryData } = useSelector((state) => state.search);

  const { countryCode } = useCountryCode();

  const inputRef = useRef();
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [countryTerm, setCountryTerm] = useState("");
  const [country, setCountry] = useState(countryData || { name: "Select Country", flag: "", code: "" });

  // Default to the UAE when nothing is chosen, so a first search works without
  // forcing a manual pick (same rule as the production hero variant).
  useEffect(() => {
    if (!countryData) {
      const ae = Countries.find((c) => c.code === "AE");
      if (ae) {
        const detail = { name: ae.name, flag: ae.file_url, code: ae.code };
        dispatch(setCountryData(detail));
        setCountry(detail);
      }
    } else {
      setCountry(countryData);
    }
  }, [countryData, dispatch]);

  /* On the results page the field describes what is on screen, so it shows the query that produced it. */
  useEffect(() => {
    if (!reflectQuery || !inputRef.current) return;
    inputRef.current.value = searchQuery || "";
  }, [searchQuery, reflectQuery]);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, []);

  const chooseCountry = (option) => {
    const detail = { name: option.name, flag: option.file_url, code: option.code };
    dispatch(setCountryData(detail));
    setCountry(detail);
    setCountryTerm("");
    setOpen(false);
  };

  /* "Locate Using GPS" resolves to the code the useCountryCode hook already
     derived, exactly as the production bar's handleLocationPopup does. */
  const handleLocateByGps = (choose) => {
    const match = Countries.find((c) => c.code === countryCode);
    if (match) choose(match);
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    const value = inputRef.current?.value?.trim() || "";
    if (value.length < 3) {
      setError("Please enter at least 3 characters.");
      return;
    }
    setError("");
    dispatch(setSearchResults([]));
    dispatch(setSearch(value));
    dispatch(setCountryData(country));
    navigate(resultsPath);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleSearch(e);
  };

  return (
    <div className={cn(compact && "relative", className)}>
      {/* `relative` so the country menu can span the whole bar on phones —
          see the static/sm:relative switch on the trigger below. */}
      <div
        className={cn(
          "relative flex items-center gap-1 border border-[var(--line)] bg-white",
          compact ? "h-[46px] rounded-lg p-1.5" : "rounded-xl p-2"
        )}
      >
        {/* Country selector */}
        {/* `static` on phones hands the menu's containing block to the bar
            above, so it can span the full field width instead of the width of
            this trigger; `sm:relative` restores anchoring on larger screens. */}
        <CountrySelect
          variant="bar"
          size={compact ? "sm" : "md"}
          /* The name costs ~150px. The page band can afford it from `sm`; the top bar shares its row with the title, the bell and the account chip, so it waits for... */
          nameClassName={compact ? "hidden md:inline" : "hidden sm:inline"}
          value={country?.code}
          onChange={chooseCountry}
          onLocate={handleLocateByGps}
        />

        <span
          className={cn("w-px shrink-0 bg-[var(--line)]", compact ? "h-5" : "h-6")}
          aria-hidden="true"
        />

        <input
          ref={inputRef}
          type="search"
          defaultValue={reflectQuery ? searchQuery || "" : ""}
          onKeyDown={onKeyDown}
          onChange={() => error && setError("")}
          placeholder={compact ? "Search companies…" : "Search by business name."}
          aria-label="Search by business name"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "u1-search-error" : undefined}
          className={cn(
            "min-w-0 grow basis-0 bg-transparent border-none focus:outline-none",
            "text-[var(--ink)] placeholder:text-[var(--muted)] font-sans",
            compact ? "h-[34px] px-2 text-[14px]" : "h-11 px-3.5 text-[15px]"
          )}
        />

        {/* Phones show the glyph alone — the label costs width the field needs.
            aria-label carries the name either way, so the button is still
            announced as "Search" when only the icon is visible. */}
        <Button
          onClick={handleSearch}
          disabled={isLoading}
          className={cn(
            "shrink-0 ml-auto justify-center",
            compact
              ? "h-[34px] w-[34px] px-0 text-[13.5px] sm:w-auto sm:px-4"
              : "w-11 px-0 sm:w-auto sm:px-6"
          )}
          aria-label="Search"
        >
          <span className="hidden sm:inline">{isLoading ? "Searching…" : "Search"}</span>
          <span className="sm:hidden flex" aria-hidden="true">
            <SearchIconCompact />
          </span>
        </Button>
      </div>

      {/* In the bar the message is taken out of flow: the row is a fixed 46px
          and anything below it would push the bell and account chip down. */}
      {error && (
        <p
          id="u1-search-error"
          role="alert"
          className={cn(
            /* Still the raw hex the rest of u1 uses for errors — there is no
               token for it yet. See FINDINGS F-005. */
            "text-[12px] text-[#D61E1E] font-sans",
            compact ? "absolute left-0 top-full mt-1" : "mt-2"
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default SearchBar;
